import React, { useEffect } from 'react';

export interface IonicStandaloneViewProps {
  children: React.ReactNode;
}

/**
 * Route wrapper for pages that use the full Ionic layout stack
 * (IonPage / IonHeader / IonContent / IonFooter).
 *
 * Background
 * ----------
 * Ionic's IonPage uses `position: absolute; contain: layout size style` and
 * IonContent's shadow-DOM scroll container uses `position: absolute; top/bottom: 0`.
 * Both need an ancestor with **explicit viewport dimensions** to size correctly.
 *
 * The global CSS overrides in index.css set `ion-app` to `position: relative`
 * and `contain: none` so that React Router pages can use normal DOM scrolling.
 * That breaks the sizing chain IonPage/IonContent depend on.
 *
 * This wrapper provides a `position: fixed; inset: 0` container
 * (.ionic-standalone-page-container) that restores the viewport-sized box
 * IonPage needs — scoped to only the pages that opt in.
 *
 * IonicRouteView vs IonicStandaloneView
 * --------------------------------------
 * - IonicRouteView: Standard path. Opts OUT of IonPage/IonContent.
 *   Uses a plain div with normal DOM scrolling. For React Router pages.
 *
 * - IonicStandaloneView (this): Ionic-full path. Opts INTO IonPage/IonContent.
 *   Provides the fixed-viewport container so Ionic layout components work.
 *   Unconditional — pages that use this have explicitly chosen Ionic layout.
 *
 * Theme opt-in (P2-12a)
 * ---------------------
 * While mounted this sets `<html data-ionic-theme="public">`, which is one of the
 * two selectors citizen-ionic.css is gated on (the other is
 * `data-platform="native"`). Every route that renders Ionic in a browser goes
 * through this wrapper — the public share pages, the unsubscribe page, the
 * citizen landing and signup, /pro/login and contact-support — so this is the one
 * place that grants them the theme.
 *
 * Decision: public pages share the CITIZEN NATIVE theme rather than getting a
 * third layer. That is already how they looked, because those variables sat on a
 * bare `:root`; the attribute makes it deliberate and, more importantly, stops
 * the native theme reaching pages that render no Ionic at all (D-11: every
 * browser gets the responsive web UI; the Ionic shell is native-only).
 *
 * It is set here rather than per page so a new Ionic route cannot forget it, and
 * removed on unmount so navigating away from the last Ionic page drops it. On
 * native it is redundant — data-platform is already "native" — and harmless.
 *
 * Migration
 * ---------
 * To migrate a page to full Ionic layout:
 *   1. Swap its route wrapper from IonicRouteView to IonicStandaloneView
 *   2. Use IonPage / IonContent in the page or its layout component
 *   3. Use Ionic UI components (IonCard, IonList, etc.) for content
 */
const THEME_ATTRIBUTE = 'data-ionic-theme';

const IonicStandaloneView: React.FC<IonicStandaloneViewProps> = ({ children }) => {
  useEffect(() => {
    const root = document.documentElement;
    // Ref-count: two Ionic views can overlap during a route transition, and the
    // outgoing one's cleanup must not strip the theme from the incoming one.
    const depth = Number(root.dataset.ionicThemeDepth ?? '0') + 1;
    root.dataset.ionicThemeDepth = String(depth);
    root.setAttribute(THEME_ATTRIBUTE, 'public');

    return () => {
      const next = Number(root.dataset.ionicThemeDepth ?? '1') - 1;
      if (next > 0) {
        root.dataset.ionicThemeDepth = String(next);
      } else {
        delete root.dataset.ionicThemeDepth;
        root.removeAttribute(THEME_ATTRIBUTE);
      }
    };
  }, []);

  return <div className="ionic-standalone-page-container">{children}</div>;
};

export default IonicStandaloneView;
